import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Check, X, EyeOff, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const REJECT_REASONS = [
  ["spam_or_advertising", "Spam or advertising"],
  ["profanity_hate_harassment", "Profanity, hate or harassment"],
  ["personal_or_private_info", "Personal or private information"],
  ["unrelated_to_hola_movers", "Unrelated to Hola Movers"],
  ["not_a_real_experience", "Not a real experience"],
  ["written_by_staff_or_family", "Written by staff or family"],
  ["submitted_without_consent", "Submitted without consent"],
];

export default function ReviewsManager() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);

  const load = () => {
    api.get(`/admin/reviews?status=${status}`).then((r) => setRows(r.data)).catch(() => {});
    api.get(`/admin/review-reports`).then((r) => setReports(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => { await api.post(`/admin/reviews/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (never for being negative):\n" + REJECT_REASONS.map(([k]) => k).join(", "));
    if (!reason) return;
    try { await api.post(`/admin/reviews/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid reason"); }
  };
  const hidePart = async (id, cur) => {
    const txt = window.prompt("Edit the visible text (personal info removed). Leave blank to show the standard hidden note.", cur || "");
    if (txt === null) return;
    await api.post(`/admin/reviews/${id}/hide-part`, { trip_text: txt }); toast.success("Updated"); load();
  };
  const reply = async (id, cur) => {
    const txt = window.prompt("Public reply (be respectful; no personal/trip details):", cur || "");
    if (txt === null) return;
    await api.post(`/admin/reviews/${id}/reply`, { business_reply: txt }); toast.success("Reply saved"); load();
  };
  const markReport = async (id) => { await api.post(`/admin/review-reports/${id}/reviewed`); load(); };

  return (
    <div data-testid="admin-reviews">
      <SectionTitle title="Reviews" subtitle="Verified client reviews — approve, reply, or hide personal info. Never reject for being negative." />

      <div className="flex gap-2 mb-4 text-sm">
        {["pending", "approved", "rejected", "removed", "all"].map((s) => (
          <button key={s} data-testid={`rev-filter-${s}`} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 ${status === s ? "bg-terracotta text-white" : "bg-sand-100 text-charcoal-600"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex text-terracotta">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill={i <= r.rating ? "currentColor" : "none"} strokeWidth={1.5} />)}</span>
              <span className="font-medium text-charcoal-800">{r.display_name}</span>
              <span className="text-xs text-charcoal-400">{r.city_from} → {r.city_to} · {r.language}</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{r.status}{r.reject_reason ? ` · ${r.reject_reason}` : ""}</span>
            </div>
            <p className="text-sm text-charcoal-700">{r.trip_text || (r.partially_hidden ? "[hidden]" : "")}</p>
            {r.pet_story && <p className="text-xs text-charcoal-500 italic mt-1">🐾 {r.pet_story}</p>}
            {r.business_reply && <p className="text-xs bg-sand-50 rounded p-2 mt-2">Reply: {r.business_reply}</p>}
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              {r.status !== "approved" && <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="bg-green-600 text-white rounded-full px-3 py-1 flex items-center gap-1"><Check size={12} /> Approve</button>}
              {r.status !== "rejected" && <button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} className="border border-red-300 text-red-600 rounded-full px-3 py-1 flex items-center gap-1"><X size={12} /> Reject</button>}
              <button onClick={() => hidePart(r.id, r.trip_text)} className="border rounded-full px-3 py-1 flex items-center gap-1"><EyeOff size={12} /> Hide part</button>
              <button onClick={() => reply(r.id, r.business_reply)} className="border rounded-full px-3 py-1 flex items-center gap-1"><MessageSquare size={12} /> Reply</button>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-sm text-charcoal-500">No reviews in this view.</p>}
      </div>

      {reports.length > 0 && (
        <div className="mt-8">
          <h3 className="font-medium text-charcoal-800 mb-2">Reported reviews</h3>
          <div className="space-y-2">
            {reports.map((rp) => (
              <div key={rp.id} className="flex items-center gap-2 text-sm bg-white border border-sand-200 rounded-lg px-3 py-2" data-testid={`report-${rp.id}`}>
                <span className="text-charcoal-600 flex-1">{rp.note || "(no note)"} · {rp.review_id}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-sand-100">{rp.status}</span>
                {rp.status === "open" && <button onClick={() => markReport(rp.id)} className="text-xs border rounded-full px-3 py-1">Mark reviewed</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
